/**
 * CollapsedListToolbar Component
 *
 * Displays icon buttons for search, model filters, and view mode selection
 * when the sidebar is collapsed on the Comparison List View (desktop only).
 */

import React from 'react';
import { ViewModeSlider } from '../ViewModeSlider';
import type { DensityOption } from '../state/density';
import type { Response } from '../../../lib/types/results';
import { ModelFilter } from '../comparisons/ModelFilter';

export interface CollapsedListToolbarProps {
  isComparisonsView: boolean;
  density: DensityOption;
  onDensityChange: (density: DensityOption) => void;
  onSearchClick: () => void;
  cmpQuery?: string;
  onCmpQueryChange?: (query: string) => void;
  // Model Filter props
  modelFilterResponses: Response[];
  modelFilterModelDetails: Array<{ id: string; displayName: string; providerName: string }>;
  modelFilterSelectedModels: Set<string>;
  modelFilterMode: 'any' | 'all';
  onModelFilterChange: (selectedModels: Set<string>, filterMode: 'any' | 'all') => void;
  onFilterClick: () => void;
}

export const CollapsedListToolbar: React.FC<CollapsedListToolbarProps> = ({
  isComparisonsView,
  density,
  onDensityChange,
  onSearchClick,
  cmpQuery = '',
  onCmpQueryChange,
  modelFilterResponses,
  modelFilterModelDetails,
  modelFilterSelectedModels,
  modelFilterMode,
  onModelFilterChange,
  onFilterClick,
}) => {
  // Only show in Comparisons view
  if (!isComparisonsView) {
    return null;
  }

  const isSearchActive = cmpQuery.length > 0;

  const handleSearchButtonClick = () => {
    if (isSearchActive && onCmpQueryChange) {
      // Clear search if already active
      onCmpQueryChange('');
    } else {
      // Open search popup if not active
      onSearchClick();
    }
  };

  return (
    <div className="pt-3 sm:pt-4 flex flex-col items-center gap-2">
      {/* Search Button */}
      <button
        onClick={handleSearchButtonClick}
        className={`w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${
          isSearchActive
            ? 'bg-cyan-500/20 border border-cyan-400/50 text-cyan-300'
            : 'bg-slate-700/30 border border-slate-600/50 text-slate-400 hover:bg-slate-700/50 hover:text-white hover:border-cyan-400/50'
        }`}
        title={isSearchActive ? "Clear search" : "Search comparisons"}
        aria-label={isSearchActive ? "Clear search" : "Search comparisons"}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </button>

      {/* Model Filter Button */}
      <button
        onClick={onFilterClick}
        className="relative w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center bg-slate-700/30 border border-slate-600/50 text-slate-400 hover:bg-slate-700/50 hover:text-white hover:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
        title="Filter by model"
        aria-label="Filter by model"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        {/* Badge showing number of selected models */}
        {modelFilterSelectedModels.size > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {modelFilterSelectedModels.size}
          </span>
        )}
      </button>

      {/* View Mode Selector */}
      <div className="mt-2">
        <ViewModeSlider
          value={density}
          onChange={onDensityChange}
          collapsed={true}
        />
      </div>
    </div>
  );
};
